import { useMemo } from 'react';
import type { Member } from '@glyph/protocol-ts';
import { fmtAgo, useAudit, useMembers } from '@glyph/core';
import { GroupsPanel, PresetsScreen, SchedulesScreen, UsersScreen, fmtDateTs } from '@glyph/screens';
import { Card, CardHeader, CellMono, EmptyState, ListRow, PageHeader, Section, SegmentedControl, StatusPill } from '@glyph/ui';

export type AdminTab = 'users' | 'presets' | 'logins' | 'groups' | 'schedules';
const TABS = [
  { value: 'users', label: 'Users' },
  { value: 'logins', label: 'Logins' },
  { value: 'groups', label: 'Groups' },
  { value: 'schedules', label: 'Schedules' },
] as const;

/** Admin console (owner / admin / platform only): accounts, sign-in activity, groups and which units are in them, caption schedules. */
export function AdminPage({ tab, onTab, onOpenGroup }: { tab: AdminTab; onTab: (t: AdminTab) => void; onOpenGroup: (id: string) => void }) {
  return (
    <>
      {tab === 'users' && <UsersScreen />}
      {tab === 'presets' && <PresetsScreen />}
      {tab === 'logins' && <LoginsTab tabs={<SegmentedControl value={tab} onChange={(v) => onTab(v as AdminTab)} options={TABS} />} />}
      {tab === 'groups' && <GroupsPanel onOpenGroup={onOpenGroup} tabs={<SegmentedControl value={tab} onChange={(v) => onTab(v as AdminTab)} options={TABS} />} />}
      {tab === 'schedules' && <SchedulesScreen tabs={<SegmentedControl value={tab} onChange={(v) => onTab(v as AdminTab)} options={TABS} />} />}
    </>
  );
}

// ---- Logins ---------------------------------------------------------------------------------------

const AUTH_ACTIONS: Record<string, { label: string; tone: 'ok' | 'warn' | 'bad' | 'fg4' }> = {
  'auth.login': { label: 'Signed in', tone: 'ok' },
  'auth.logout': { label: 'Signed out', tone: 'fg4' },
  'auth.login_failed': { label: 'Wrong password', tone: 'warn' },
  'auth.lockout': { label: 'Locked out', tone: 'bad' },
  'invite.accept': { label: 'Joined', tone: 'ok' },
  'invite.create': { label: 'Invited', tone: 'fg4' },
};

function LoginsTab({ tabs }: { tabs: React.ReactNode }) {
  const members = useMembers();
  const audit = useAudit(300);
  const byId = useMemo(() => new Map((members.data ?? []).map((m) => [m.id, m])), [members.data]);
  const rows = useMemo(
    () =>
      (audit.data ?? [])
        .filter((a) => a.action in AUTH_ACTIONS)
        .map((a) => ({ ...a, who: byId.get(a.actor)?.email ?? (a.target.startsWith('user ') ? byId.get(a.target.slice(5))?.email : undefined) ?? a.actor })),
    [audit.data, byId],
  );
  const accounts = (members.data ?? []).filter((m) => m.status === 'active');
  const seen = (u: Member) => (u.last_seen ? fmtAgo(u.last_seen) : 'never');
  const ip = (detail: string) => detail.match(/ip=([^ ·]+)/)?.[1] ?? '';
  return (
    <Section>
      <PageHeader desktopOnly title="Logins" meta={`${accounts.length} ACCOUNTS`} actions={tabs} />
      <div className="md:hidden">{tabs}</div>
      <div className="flex flex-wrap items-start gap-5">
        <Card className="min-w-0" style={{ flex: '1 1 520px' }}>
          <CardHeader title="Recent activity" meta={`${rows.length}`} />
          {rows.length === 0 && <EmptyState label="No sign-ins yet" />}
          {rows.slice(0, 100).map((a) => {
            const k = AUTH_ACTIONS[a.action] ?? { label: a.action, tone: 'fg4' as const };
            return (
              <ListRow
                key={a.id}
                title={a.who}
                sub={`${fmtDateTs(a.ts)}${ip(a.detail) ? ` · ${ip(a.detail)}` : ''}`}
                trailing={<StatusPill tone={k.tone}>{k.label}</StatusPill>}
              />
            );
          })}
        </Card>
        <Card className="min-w-[260px]" style={{ flex: '0 1 340px' }}>
          <CardHeader title="Last sign-in" />
          {accounts.map((u) => (
            <ListRow key={u.id} title={u.name} sub={u.email} trailing={<CellMono>{seen(u)}</CellMono>} />
          ))}
        </Card>
      </div>
    </Section>
  );
}
